<script setup>
import ComboBoxComponent from '@/components/ComboBoxWithRegistComponent.vue';
import { useCommonCodeStore } from '@/store/commoncode-store';
import { computed, onMounted, ref, watch } from 'vue';

// Props 정의
const props = defineProps({
    modelValue: {
        type: [Object, Array, String],
        default: () => []
    },
    inputId: {
        type: String,
        default: ''
    },
    options: {
        type: Array,
        default: () => []
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    readonly: {
        type: Boolean,
        default: false
    },
    errmsg: {
        type: String,
        default: ''
    }
});

// Emits 정의
const emit = defineEmits(['update:modelValue', 'input', 'click', 'select', 'update-options']);
const isReadonly = computed(() => props.readonly);
const inputValue = ref(props.modelValue);
const selectValue = ref('');
const errMessage = ref(props.errmsg);
const ValidationMsg = ref('');
const vehicleOptionList = ref([]);
const selectedItems = ref([]);
const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);

onMounted(() => {
    selectValue.value = '';
    setvehicleOption();
    initializeSelectedItems();
});

const setvehicleOption = () => {
    if (props.options && props.options.length > 0) {
        vehicleOptionList.value = props.options.map((option) => ({
            value: option.value,
            text: option.text || option.disptext
        }));
    }
};

const initializeSelectedItems = () => {
    selectedItems.value = []; // 선택된 항목 초기화

    if (Array.isArray(props.modelValue)) {
        props.modelValue.forEach((value) => {
            const item = vehicleOptionList.value.find((option) => option.value === value);
            if (item) {
                selectedItems.value.push(item);
            }
        });
    } else if (props.modelValue) {
        const item = vehicleOptionList.value.find((option) => option.value === props.modelValue);
        if (item) {
            selectedItems.value.push(item);
        }
    }

    emitUpdate(selectedItems.value);
};

const emitUpdate = (value) => {
    const emitValue = value.map((item) => item.value);
    inputValue.value = emitValue;
    emit('update:modelValue', emitValue);
    emit('input', {
        id: props.inputId,
        value: emitValue
    });
};

const selectItem = (event) => {
    const selectedValue = event.value;
    const selectedItem = vehicleOptionList.value.find((item) => item.value === selectedValue);

    if (selectedItem && !selectedItems.value.some((item) => item.value === selectedValue)) {
        selectedItems.value.push(selectedItem);
        emitUpdate(selectedItems.value);
    }
    //selectValue.value = ''; // 선택 후 combobox 초기화
};

const removeSelectedItem = (index) => {
    selectedItems.value.splice(index, 1);
    emitUpdate(selectedItems.value);
};

const removeAll = () => {
    if (selectedItems.value.length === 0) return;
    selectedItems.value = [];
    emitUpdate(selectedItems.value);
};

const selectAll = () => {
    selectedItems.value = vehicleOptionList.value.map((option) => ({
        value: option.value,
        text: option.text
    }));
    emitUpdate(selectedItems.value);
};

watch(
    () => props.modelValue,
    (newVal) => {
        if (Array.isArray(newVal)) {
            selectedItems.value = newVal.map((value) => {
                const item = vehicleOptionList.value.find((option) => option.value === value);
                return item || { value, text: value };
            });
        } else if (newVal) {
            const item = vehicleOptionList.value.find((option) => option.value === newVal);
            selectedItems.value = item ? [item] : [{ value: newVal, text: newVal }];
        } else {
            selectedItems.value = [];
        }
    },
    { immediate: true }
);

watch(
    () => props.errmsg,
    (newVal) => {
        ValidationMsg.value = newVal || '';
        errMessage.value = newVal || '';
    },
    { immediate: true, deep: true }
);
</script>

<template>
    <div
        class="flex flex-col"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <!-- <label :for="inputId" class="label">
            {{ label }}
            <span v-if="required" class="required-asterisk">*</span>
            <span v-if="ValidationMsg" class="validation-message">{{ ValidationMsg }}</span>
        </label> -->

        <ComboBoxComponent inputId="combobox" :modelValue="selectValue" :options="vehicleOptionList" class="combobox-container" :label="props.label" selectAll="선택" @change="selectItem"> </ComboBoxComponent>
        <div class="select-count"><button @click="selectAll()" class="selectall-button">전체선택</button><button @click="removeAll()" class="removeall-button">전체취소</button> {{ selectedItems.length }} 건</div>
        <div class="selected-items-container">
            <div v-for="(item, index) in selectedItems" :key="index" class="selected-item">
                <span class="item-text">{{ item.text }}</span>
                <button @click="removeSelectedItem(index)" class="remove-button">X</button>
            </div>
        </div>
    </div>
</template>

<style scoped>
.label {
    font-family: 'SCoreDream600';
    display: flex;
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171;
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.selectbox-container {
    display: flex;
    width: 100%;
}

.button-grid {
    display: grid;
    gap: 0.5rem;
}

.button-row {
    display: flex;
    gap: 0.5rem;
}

.grid-button {
    flex: 1;
    padding: 0.3rem;
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    color: #374151;
    background-color: white;
    border: 0.1rem solid #d1d5db;
    border-radius: 0.3rem;
    cursor: pointer;
    transition:
        background-color 0.2s,
        box-shadow 0.2s;
    min-height: 2rem;
    display: flex;
    align-items: center;
    justify-content: center;
}

.grid-button:hover {
    background-color: #eef0f1;
}

.grid-button:focus {
    outline: none;
}

.grid-button.selected {
    box-shadow: 0 0 0 0.1rem #0038ea;
    opacity: 0.9;
}

.selected-items-container {
    display: flex;
    flex-wrap: wrap;
    gap: 0.2rem;
    margin-top: 0.1rem;
    height: 8.5rem;
    overflow-y: auto;
    padding: 0.5rem 0.4rem;
    border: 0.1rem solid #d1d5db;
    border-radius: 0.5rem;
    justify-content: flex-start;
    align-content: flex-start;
    width: 100%;
}

.selected-item {
    border: 0.1rem solid #d1d5db;
    padding: 0.6rem;
    border-radius: 0.3rem;
    display: flex;
    align-items: center;
    justify-content: space-between;
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    height: 2rem;
    margin-right: 0.2rem;
    margin-bottom: 0.2rem;
    width: calc(50% - 0.3rem);
    box-sizing: border-box;
}

.item-text {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    flex-grow: 1;
    margin-left: 0.3rem;
    cursor: pointer;
}

.item-text:hover {
    text-decoration: underline;
    color: #0038ea;
}

.remove-button {
    margin-left: 0.5rem;
    color: red;
    width: 0.5rem;
    font-family: 'SCoreDream400';
    font-size: 0.75rem;
    display: flex;
    justify-content: center;
    align-items: center;
    cursor: pointer;
    background: none;
    border: none;
    margin-right: 0.2rem;
}

.remove-button:hover {
    font-family: 'SCoreDream600';
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.grid-button.disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.select-count {
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    opacity: 0.7;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    margin-bottom: 0.35rem;
    margin-top: 1rem;
    margin-right: 0.25rem;
    height: 0.5rem;
}

.selectall-button {
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    opacity: 0.8;
    cursor: pointer;
    margin-right: 0.4rem;
}

.selectall-button:hover {
    opacity: 1;
    cursor: pointer;
}

.removeall-button {
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    opacity: 0.8;
    cursor: pointer;
    margin-right: 1rem;
}

.removeall-button:hover {
    opacity: 1;
    cursor: pointer;
}
</style>
